/**
 * Pickup and delivery trucking as the results show it: one leg before and after
 * an air or ocean quote's own legs, built from the quote's own `door_to_door` and the search's
 * `access_legs` (which carry the carrier, the display text and the reason a leg
 * has no price). The wording of every reason is ours: the API gives a fixed
 * `kind`, never a vendor's text.
 */
import dayjs from 'dayjs';

import type {
  AccessLeg,
  AccessLegs,
  NormalizedQuote,
  TruckingCaveat,
  TruckingLeg,
  TruckingOption,
  TruckingQuote,
} from '../../../apis/lanePlanner';
import { providerLogoFor } from '../../../common/utils/providerLogo';
import { formatKgCo2e } from '@/utils/formatEmissions';
import { legLocalTimeOf } from './localTime';
import { emissionsDisclosureOf, formatMoney } from './quoteView';

const REASONS: Record<string, string> = {
  outside_us: 'trucking is only quoted within the US.',
  no_address: 'there is no trucking address for this airport or port.',
  bad_address:
    'the address could not be read. Include the street and ZIP code.',
  not_searched: 'this airport or port was not searched.',
  not_connected: 'no trucking vendor is connected.',
  credentials: 'the trucking vendor rejected the saved login.',
  unsupported: 'the trucking vendor cannot quote this shipment.',
  refused: 'the trucking vendor declined this lane.',
  no_rates: 'the trucking vendor had no rates for this lane.',
  timeout: 'the trucking vendor timed out. Search again.',
  unavailable: 'the trucking vendor is unavailable. Search again later.',
  error: 'it could not be quoted. Search again.',
};

const CAVEATS: Record<TruckingCaveat, string> = {
  hazmat: 'hazmat',
  temperature_controlled: 'temperature-controlled cargo',
  uld: 'ULD cargo',
  fcl: 'container loads',
};

/** Why a leg has no price: the API's `kind`, else a sentence fit for how the leg ended. */
const reasonOf = (entry: AccessLeg): string => {
  if (entry.kind) return REASONS[entry.kind] ?? REASONS.error;
  return entry.status === 'unavailable'
    ? 'trucking is not available for this airport or port.'
    : 'no trucking rate was returned.';
};

const NONE_IN_TIME = 'no trucking rate arrives in time for this departure.';

const LEG_TITLES: Record<TruckingLeg, string> = {
  pickup: 'Pickup',
  delivery: 'Delivery',
};

/** The option picked per trucking leg and site, by the stored quote's id. */
export type TruckingChoices = Record<string, string>;

export const choiceKey = (leg: TruckingLeg, site: string): string =>
  `${leg}:${site}`;

/** Whether a trucking option works with the flight or voyage it connects to; see `fitOf`. */
export type TruckingFit = (option: TruckingQuote) => boolean;

/** The options of a leg that fit, cheapest first; `best` alone for a backend that sends no list. */
export const fittingOptionsOf = (
  entry: AccessLeg,
  fits: TruckingFit = () => true
): Array<TruckingQuote | TruckingOption> =>
  (entry.options ?? (entry.best ? [entry.best] : [])).filter(fits);

/** The picked option of a leg among those that fit; the cheapest when none is picked or the pick is gone or does not fit. */
export const pickedOf = (
  entry: AccessLeg,
  choice: string | undefined,
  fits?: TruckingFit
): TruckingQuote | TruckingOption | undefined => {
  const options = fittingOptionsOf(entry, fits);
  return (
    options.find((option) => 'id' in option && option.id === choice) ??
    options[0]
  );
};

export interface TruckingLegView {
  leg: TruckingLeg;
  site: string;
  from: string;
  to: string;
  carrier: string | null;
  /** The product's logo for the carrier, when it has one. */
  logoUrl: string | null;
  serviceLevel: string | null;
  /** Why the leg has no price, or that it is being quoted; null for a priced leg. */
  reason: string | null;
  /** The leg's price, formatted; null while pending or when it has none. */
  price: string | null;
  /** A note on a priced leg (the ZIP was the city centre's). */
  note: string | null;
  /** The leg's CO₂ estimate, formatted; null when it has none. */
  co2: string | null;
  /** The caveat to show with `co2`. */
  co2Disclosure: string | null;
  transitMinutes: number | null;
  /** More than one carrier is priced, so the leg can be changed. */
  canChange: boolean;
}

/** The airport or port a trucking leg serves: the priced one, else the quote's own end. */
const siteOf = (quote: NormalizedQuote, leg: TruckingLeg): string | undefined =>
  quote.door_to_door?.[leg]?.site ??
  (leg === 'pickup' ? quote.route?.origin : quote.route?.destination);

/** The trucking legs of a quote, pickup then delivery; empty when the search asked for no trucking. */
export const truckingLegsOf = (
  quote: NormalizedQuote,
  accessLegs: AccessLegs | undefined,
  choices: TruckingChoices = {},
  shipDate?: string
): TruckingLegView[] => {
  const door = quote.door_to_door;
  if (!accessLegs || !door) return [];
  const views: TruckingLegView[] = [];
  (['pickup', 'delivery'] as const).forEach((leg) => {
    const site = siteOf(quote, leg);
    const entry = site ? accessLegs[leg]?.[site] : undefined;
    // a city is not a door: no leg is drawn, and `truckingHintsOf` asks for an address
    if (!site || !entry || entry.kind === 'city_only') return;
    const fits = fitOf(quote, leg, shipDate);
    const picked =
      entry.status === 'priced'
        ? pickedOf(entry, choices[choiceKey(leg, site)], fits)
        : undefined;
    views.push({
      leg,
      site,
      from: entry.from,
      to: entry.to,
      carrier: picked?.carrier ?? null,
      logoUrl: providerLogoFor(picked?.carrier, 'road'),
      serviceLevel: picked?.service_level ?? null,
      reason: picked
        ? null
        : entry.status === 'pending'
          ? `Quoting ${leg} trucking…`
          : `${LEG_TITLES[leg]} trucking not quoted: ${
              entry.status === 'priced' && leg === 'pickup'
                ? NONE_IN_TIME
                : reasonOf(entry)
            }`,
      price: picked ? formatMoney(picked.price, picked.currency) : null,
      note:
        picked && entry.city_centre ? 'Priced at the city centre ZIP.' : null,
      co2: formatKgCo2e(picked?.co2_kg),
      co2Disclosure: picked?.co2_disclosure ?? null,
      transitMinutes: picked?.transit_minutes ?? null,
      canChange: (entry.options?.filter(fits).length ?? 0) > 1,
    });
  });
  return views;
};

const ADDRESS_HINTS: Record<TruckingLeg, string> = {
  pickup: 'Add an origin address for pickup trucking quotes',
  delivery: 'Add a destination address for delivery trucking quotes',
};

/** One line per trucking leg left out because only a city was given for its end. */
export const truckingHintsOf = (
  quote: NormalizedQuote,
  accessLegs: AccessLegs | undefined
): string[] => {
  const door = quote.door_to_door;
  if (!accessLegs || !door) return [];
  return (['pickup', 'delivery'] as const)
    .filter((leg) => {
      const site = siteOf(quote, leg);
      return site && accessLegs[leg]?.[site]?.kind === 'city_only';
    })
    .map((leg) => ADDRESS_HINTS[leg]);
};

/** `day`, or the Monday after when it is a weekend. */
const businessDayFrom = (day: dayjs.Dayjs): dayjs.Dayjs =>
  day.day() === 0
    ? day.add(1, 'day')
    : day.day() === 6
      ? day.add(2, 'day')
      : day;

/** The day a truck leaves: a pickup truck on the ship date; a delivery truck on the quote's arrival day, or the next business day when that is a weekend. */
export const pickupDayOf = (
  leg: TruckingLeg,
  shipDate: string | undefined,
  arrivalDay?: string | null
): dayjs.Dayjs | null => {
  const source = leg === 'pickup' ? shipDate : arrivalDay;
  const day = source ? dayjs(source) : null;
  if (!day?.isValid()) return null;
  return leg === 'pickup' ? day : businessDayFrom(day);
};

/** The day a pickup truck arrives: its pickup day plus the transit rounded up to whole business days (at least one). */
export const arrivalOf = (
  pickup: dayjs.Dayjs | null,
  transitMinutes: number | null
): dayjs.Dayjs | null => {
  if (!pickup || transitMinutes == null || transitMinutes <= 0) return null;
  let day = pickup;
  for (let left = Math.max(1, Math.ceil(transitMinutes / 1440)); left > 0;) {
    day = day.add(1, 'day');
    if (day.day() !== 0 && day.day() !== 6) left -= 1;
  }
  return day;
};

/** The calendar day (YYYY-MM-DD) a quote's first leg departs, at its origin; null when unknown. */
export const departureDayOf = (quote: NormalizedQuote): string | null =>
  legLocalTimeOf(quote, 0, 'departs')?.day ?? null;

/** Whether a pickup arriving on `arrival` is later, by calendar date, than the departure day it feeds; the same day is on time, and an unknown either way is not late. */
export const arrivesAfter = (
  arrival: dayjs.Dayjs | null,
  departureDay: string | null | undefined
): boolean =>
  !!arrival && !!departureDay && arrival.format('YYYY-MM-DD') > departureDay;

/** The calendar day (YYYY-MM-DD) a quote's last leg arrives, at its destination; null when unknown. */
export const arrivalDayOf = (quote: NormalizedQuote): string | null =>
  legLocalTimeOf(quote, quote.legs.length - 1, 'arrives')?.day ?? null;

/** Whether a delivery truck leaving on `departure` leaves on the first business day from the arrival day or the business day after it; an unknown either way fits. */
export const departsInTime = (
  departure: dayjs.Dayjs | null,
  arrivalDay: string | null | undefined
): boolean => {
  if (!departure || !arrivalDay) return true;
  const first = businessDayFrom(dayjs(arrivalDay));
  const day = departure.format('YYYY-MM-DD');
  return [first, businessDayFrom(first.add(1, 'day'))].some(
    (allowed) => allowed.format('YYYY-MM-DD') === day
  );
};

/**
 * Whether a trucking option works with the quote's flight or voyage: a pickup
 * truck arrives by the departure day, a delivery truck leaves on the arrival
 * day or the next. The dates are the quote's own, so this is per quote.
 * The delivery check is display-only until an option has its own pickup date.
 */
export const fitOf =
  (quote: NormalizedQuote, leg: TruckingLeg, shipDate?: string): TruckingFit =>
  (option) =>
    leg === 'pickup'
      ? !arrivesAfter(
          arrivalOf(pickupDayOf(leg, shipDate), option.transit_minutes),
          departureDayOf(quote)
        )
      : departsInTime(
          pickupDayOf(leg, shipDate, arrivalDayOf(quote)),
          arrivalDayOf(quote)
        );

const cents = (amount: string): number => Math.round(Number(amount) * 100);

export interface TruckingTotal {
  label: string;
  value: string;
  note: string | null;
  co2: string | null;
  co2Note: string | null;
  co2Disclosure: string | null;
}

/** The caveats as one text, each sentence once, at its last place (the shared "Not included" closes it). */
const joinDisclosures = (
  texts: (string | null | undefined)[]
): string | null => {
  const sentences = texts.flatMap(
    (text) => text?.replace(/\.\s+/g, '.\n').split('\n') ?? []
  );
  const kept = sentences.filter(
    (sentence, i) => sentence && sentences.lastIndexOf(sentence) === i
  );
  return kept.length ? kept.join(' ') : null;
};

/**
 * The door-to-door total, once known, with the legs it leaves out. A picked
 * option replaces the cheapest in the backend's total, and a leg with no option
 * that fits the quote's dates drops out of it: the fit is per quote, so the
 * backend (which prices each airport or port once) cannot know it. One priced
 * in another currency than the quote leaves the total unknown, as the backend does.
 * `co2` adds up the estimates of the same legs and `co2Note` names those that
 * have none; `co2` is null when no trucking leg has one, since it would only
 * repeat the quote's own.
 */
export const truckingTotalOf = (
  quote: NormalizedQuote,
  accessLegs?: AccessLegs,
  choices: TruckingChoices = {},
  shipDate?: string
): TruckingTotal | null => {
  const door = quote.door_to_door;
  if (!door) return null;
  // no backend total (a currency mismatch): rebuild it from the quote's own price
  const known = door.total != null;
  const currency = known ? door.currency : quote.currency;
  const base = door.total ?? quote.price;
  if (base == null) return null;
  let total = cents(base);
  let co2 = quote.co2_kg;
  const co2Missing: string[] = co2 == null ? [`${quote.mode} freight`] : [];
  const co2Legs: (string | null | undefined)[] = [];
  const excluded = new Set<TruckingLeg>(door.excludes);
  for (const leg of ['pickup', 'delivery'] as const) {
    const priced = door[leg];
    const entry = priced ? accessLegs?.[leg][priced.site] : undefined;
    if (!priced || !entry) {
      if (!known && !excluded.has(leg)) return null;
      // in the total, but no entry to read its CO₂ from: named, not summed
      if (priced && !excluded.has(leg)) co2Missing.push(leg);
      continue;
    }
    const picked = pickedOf(
      entry,
      choices[choiceKey(leg, priced.site)],
      fitOf(quote, leg, shipDate)
    );
    if (!picked) {
      if (known) total -= cents(priced.price);
      excluded.add(leg);
    } else {
      if (picked.currency !== currency) return null;
      total += cents(picked.price) - (known ? cents(priced.price) : 0);
      if (picked.co2_kg != null) {
        co2 = (co2 ?? 0) + picked.co2_kg;
        co2Legs.push(picked.co2_disclosure);
      } else co2Missing.push(leg);
    }
  }
  const left = (['pickup', 'delivery'] as const).filter((leg) =>
    excluded.has(leg)
  );
  return {
    label: 'Door-to-door total',
    value: formatMoney((total / 100).toFixed(2), currency),
    note: left.length ? `excludes ${left.join(' and ')}` : null,
    ...(co2Legs.length
      ? {
          co2: formatKgCo2e(co2),
          co2Note: co2Missing.length
            ? `excludes ${co2Missing.join(' and ')}`
            : null,
          co2Disclosure: joinDisclosures([
            quote.co2_kg != null ? emissionsDisclosureOf(quote) : null,
            ...co2Legs,
          ]),
        }
      : { co2: null, co2Note: null, co2Disclosure: null }),
  };
};

/** A warning when the cargo is something standard dry-van LTL does not fit; null when it is not. */
export const truckingCaveatText = (
  accessLegs: AccessLegs | undefined
): string | null => {
  const named = (accessLegs?.caveats ?? [])
    .map((caveat) => CAVEATS[caveat])
    // a caveat added by a newer backend is not named here: leave it out
    .filter((name): name is string => !!name);
  return named.length
    ? `Trucking is priced as standard dry-van LTL, which may not suit ${named.join(' and ')}.`
    : null;
};

/** Plain-text lines for the share summary. */
export const truckingShareLines = (
  quote: NormalizedQuote,
  accessLegs: AccessLegs | undefined,
  choices: TruckingChoices = {},
  shipDate?: string
): string[] => {
  const lines = truckingLegsOf(quote, accessLegs, choices, shipDate)
    .filter((view) => view.price !== null)
    .map(
      (view) =>
        `${LEG_TITLES[view.leg]} trucking: ${view.from} → ${view.to}${view.carrier ? `, ${view.carrier}` : ''}: ${view.price} (estimated)`
    );
  const total = truckingTotalOf(quote, accessLegs, choices, shipDate);
  if (lines.length && total) {
    lines.push(
      `${total.label}: ${total.value} (estimated)${total.note ? `, ${total.note}` : ''}`
    );
  }
  return lines;
};
